import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router';
import {
  investigate,
  type InvestigationResult,
  type InvestigationType,
  type SessionSummary,
} from '../../services/investigations';
import type { Decision } from '../../services/sessions';
import { getIncident } from '../../services/incidents';
import { resumirIncidente, contagemLegivel, type ResumoIncidente } from '../../lib/eventHumanizer';
import { SessionDecisionModal } from '../Sessions/SessionDecisionModal';

// Cockpit de investigação — uma tela onde o operador clica num IP / máquina /
// conta e vê TUDO: veredito, sessões (clicáveis → decisão/infrações/eventos),
// incidentes (expandem no lugar) e alvos correlacionados. Linguagem simples,
// foco em decidir com base no dado.
//   /investigations/ip/{value}
//   /investigations/hwid/{value}
//   /investigations/account_id/{value}

const VALID_TYPES: InvestigationType[] = ['ip', 'hwid', 'account_id'];

const TYPE_LABEL: Record<InvestigationType, string> = {
  ip: 'IP',
  hwid: 'Máquina (HWID)',
  account_id: 'Conta',
};

const DECISION_COLORS: Record<string, string> = {
  ALLOW: 'bg-emerald-900 text-emerald-300 border-emerald-700',
  FLAG: 'bg-amber-900 text-amber-300 border-amber-700',
  KICK: 'bg-orange-900 text-orange-300 border-orange-700',
  BLOCK: 'bg-red-900 text-red-300 border-red-700',
  FORCE_UPDATE: 'bg-blue-900 text-blue-300 border-blue-700',
};

function sevInfo(sev?: number): { label: string; cls: string } {
  const s = sev ?? 0;
  if (s >= 90) return { label: 'crítico', cls: 'bg-red-900 text-red-300 border-red-700' };
  if (s >= 70) return { label: 'alto', cls: 'bg-orange-900 text-orange-300 border-orange-700' };
  if (s >= 30) return { label: 'suspeito', cls: 'bg-amber-900 text-amber-300 border-amber-700' };
  return { label: 'baixo', cls: 'bg-emerald-900 text-emerald-300 border-emerald-700' };
}

function humanTime(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.round(h / 24)} d`;
}

function RiskBar({ score }: { score: number }) {
  const pct = Math.min(100, Math.max(0, score));
  const color =
    score >= 86 ? 'bg-red-500' :
    score >= 61 ? 'bg-orange-500' :
    score >= 31 ? 'bg-amber-500' :
    'bg-emerald-500';
  return (
    <div className="flex items-center gap-2 shrink-0" title="Risco da sessão (0–100)">
      <div className="w-16 h-2 bg-gray-800 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-300 tabular-nums w-7 text-right">{score}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string | undefined }) {
  return (
    <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg px-3 py-2">
      <div className="text-xl font-bold text-gray-100 tabular-nums">{value ?? '—'}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
    </div>
  );
}

function Card({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-gray-200">{title}</h2>
        {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Related({
  title,
  items,
  to,
  short,
}: {
  title: string;
  items: string[];
  to: (v: string) => string;
  short?: boolean;
}) {
  return (
    <div>
      <h3 className="text-xs text-gray-500 mb-1.5">
        {title} ({items.length})
      </h3>
      {items.length === 0 ? (
        <p className="text-gray-600 text-xs">nenhum</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.slice(0, 40).map((v) => (
            <Link
              key={v}
              to={to(v)}
              className="text-[11px] px-2 py-0.5 rounded bg-[#0d0f12] border border-[#2a2f36] text-blue-300 hover:border-blue-600 font-mono"
              title={v}
            >
              {short ? v.slice(0, 12) + '…' : v}
            </Link>
          ))}
          {items.length > 40 && <span className="text-[11px] text-gray-600 px-1">+{items.length - 40}</span>}
        </div>
      )}
    </div>
  );
}

export function Investigation() {
  const { type, value } = useParams<{ type: string; value: string }>();
  const [data, setData] = useState<InvestigationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openSession, setOpenSession] = useState<SessionSummary | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [resumos, setResumos] = useState<Record<string, ResumoIncidente | 'loading'>>({});

  useEffect(() => {
    if (!type || !value || !VALID_TYPES.includes(type as InvestigationType)) return;
    setLoading(true);
    setError(null);
    setData(null);
    setExpanded({});
    setResumos({});
    investigate(type as InvestigationType, value)
      .then(setData)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [type, value]);

  // Maior risco primeiro — é o que o operador quer ver antes de decidir.
  const sessions = useMemo(() => {
    const list = data?.sessions.data ?? [];
    return [...list].sort((a, b) => b.risk_score - a.risk_score);
  }, [data]);

  function toggleIncident(id: string): void {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!resumos[id]) {
      setResumos((prev) => ({ ...prev, [id]: 'loading' }));
      getIncident(id)
        .then((d) =>
          setResumos((prev) => ({ ...prev, [id]: resumirIncidente(d.evidence_events ?? []) })),
        )
        .catch(() => setResumos((prev) => ({ ...prev, [id]: resumirIncidente([]) })));
    }
  }

  if (!type || !value) return <div className="p-6 text-red-400">Faltando parâmetro type/value.</div>;
  if (!VALID_TYPES.includes(type as InvestigationType)) {
    return <div className="p-6 text-red-400">Tipo inválido: {type} (esperado: ip | hwid | account_id).</div>;
  }
  if (loading) return <div className="p-6 text-gray-400">Carregando investigação…</div>;
  if (error) return <div className="p-6 text-red-400">Erro: {error}</div>;
  if (!data) return <div className="p-6 text-gray-400">Sem dados.</div>;

  const rep = data.target.reputation;
  const sev = sevInfo(rep.max_severity);
  const it = type as InvestigationType;

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {/* Cabeçalho */}
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500">Investigação · {TYPE_LABEL[it]}</div>
        <h1 className="text-2xl font-bold text-gray-100 font-mono break-all">{value}</h1>
      </div>

      {/* Veredito rápido — bate o olho e entende a gravidade */}
      <div className={`rounded-lg border px-4 py-3 ${sev.cls}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-lg font-bold uppercase">{sev.label}</span>
          <span className="text-sm opacity-90">
            severidade máxima {rep.max_severity ?? '—'}
            {rep.incident_count !== undefined && ` · ${rep.incident_count} incidente(s)`}
            {rep.hard_incident_count_24h ? ` · ${rep.hard_incident_count_24h} grave(s) nas últimas 24h` : ''}
          </span>
        </div>
      </div>

      {/* Resumo em números legíveis */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Sessões" value={rep.session_count ?? data.sessions.total} />
        <Stat label="Contas distintas" value={rep.distinct_accounts_count} />
        <Stat label="Máquinas (HWID)" value={rep.distinct_hwids_count} />
        <Stat label="IPs distintos" value={rep.distinct_ips_count} />
        <Stat label="Incidentes" value={rep.incident_count ?? data.incidents.total} />
        <Stat label="Graves (total)" value={rep.hard_incident_count} />
        <Stat label="Graves 24h" value={rep.hard_incident_count_24h} />
        <Stat label="Visto por último" value={humanTime(rep.last_seen_at)} />
      </div>
      {Object.keys(rep).length === 0 && (
        <p className="text-xs text-gray-500 -mt-2">
          Sem dados de reputação ainda — a visão consolidada atualiza periodicamente. As listas abaixo são ao vivo.
        </p>
      )}

      {/* Sessões — clicáveis */}
      <Card
        title={`Sessões (${sessions.length}${data.sessions.total > sessions.length ? ` de ${data.sessions.total}` : ''})`}
        hint="Clique numa sessão para ver decisão, infrações e eventos — sem sair daqui."
      >
        {sessions.length === 0 ? (
          <p className="text-gray-500 text-sm">Nenhuma sessão para este alvo.</p>
        ) : (
          <div className="divide-y divide-[#2a2f36]">
            {sessions.map((s) => {
              const ident = s.user_id || s.account_id || null;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setOpenSession(s)}
                  className="w-full text-left flex items-center gap-3 py-2.5 px-2 -mx-2 rounded hover:bg-[#0d0f12]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-100 truncate">
                      {ident ?? <span className="text-gray-500 italic">sem login vinculado</span>}
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono truncate">
                      {s.ip || 'sem IP'} · HWID {s.hwid ? `${s.hwid.slice(0, 12)}…` : '—'} · {humanTime(s.last_heartbeat_at)}
                    </div>
                  </div>
                  <RiskBar score={s.risk_score} />
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded border shrink-0 ${
                      DECISION_COLORS[s.decision] || 'bg-gray-800 text-gray-400 border-gray-700'
                    }`}
                    title={s.decision_real !== s.decision ? `Decisão real (sem beta): ${s.decision_real}` : undefined}
                  >
                    {s.decision}
                  </span>
                  <span className="text-gray-600 shrink-0">›</span>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Incidentes — expandem no lugar */}
      <Card
        title={`Incidentes (${data.incidents.data.length}${data.incidents.total > data.incidents.data.length ? ` de ${data.incidents.total}` : ''})`}
        hint="Clique para ver o que aconteceu, ali mesmo."
      >
        {data.incidents.data.length === 0 ? (
          <p className="text-gray-500 text-sm">Nenhum incidente para este alvo.</p>
        ) : (
          <ul className="space-y-2">
            {data.incidents.data.map((i) => {
              const info = sevInfo(i.severity_max);
              const r = resumos[i.id];
              const isOpen = expanded[i.id];
              return (
                <li key={i.id} className="bg-[#0d0f12] border border-[#2a2f36] rounded">
                  <button
                    type="button"
                    onClick={() => toggleIncident(i.id)}
                    className="w-full text-left p-3 flex items-start gap-2 hover:bg-[#12151a]"
                  >
                    <span className="text-gray-500 mt-0.5 shrink-0">{isOpen ? '▾' : '▸'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-100 font-medium">{i.attack_category}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${info.cls}`}>
                          {info.label} {i.severity_max}
                        </span>
                        <span className="text-xs text-gray-500">
                          {contagemLegivel(i.event_count, i.occurrence_sum)}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-400">
                          {i.status === 'open' ? 'em aberto' : i.status}
                        </span>
                      </div>
                      {i.summary && <p className="text-xs text-gray-500 mt-0.5">{i.summary}</p>}
                      <div className="text-[10px] text-gray-600 mt-0.5">{humanTime(i.last_seen_at)}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#2a2f36] p-3 space-y-1">
                      {r === undefined || r === 'loading' ? (
                        <p className="text-gray-500 text-xs">Carregando o que aconteceu…</p>
                      ) : (
                        <>
                          <p className="text-sm text-gray-100">{r.titulo}</p>
                          {r.linhas.slice(1).map((l) => (
                            <p key={l.titulo} className="text-xs text-gray-400">
                              {l.vezes > 1 ? `${l.vezes}× ` : ''}
                              {l.titulo}
                            </p>
                          ))}
                          {r.soOperacional && (
                            <p className="text-[11px] text-gray-500 italic mt-1">
                              Nada aqui é conduta do jogador — é ruído do sistema.
                            </p>
                          )}
                          <Link
                            to={`/incidents/${i.id}`}
                            className="inline-block text-xs text-blue-400 hover:underline mt-1"
                          >
                            Abrir página completa do incidente →
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Correlação — pivô pra continuar investigando */}
      <Card title="Também visto com" hint="Contas, máquinas e IPs ligados a este alvo. Clique para investigar cada um.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Related
            title="Contas"
            items={data.cross_correlation.accounts}
            to={(a) => `/investigations/account_id/${encodeURIComponent(a)}`}
          />
          <Related
            title="Máquinas (HWID)"
            items={data.cross_correlation.hwids}
            to={(h) => `/investigations/hwid/${encodeURIComponent(h)}`}
            short
          />
          <Related
            title="IPs"
            items={data.cross_correlation.ips}
            to={(ip) => `/investigations/ip/${encodeURIComponent(ip)}`}
          />
        </div>
      </Card>

      {openSession && (
        <SessionDecisionModal
          sessionUuid={openSession.id}
          initialHint={{
            risk_score: openSession.risk_score,
            peak_risk_score: openSession.risk_score,
            decision: openSession.decision as Decision,
            decision_real: openSession.decision_real as Decision,
            user_id: openSession.user_id || '',
            account_id: openSession.account_id || openSession.user_id || '',
            hwid: openSession.hwid || '',
          }}
          onClose={() => setOpenSession(null)}
        />
      )}
    </div>
  );
}

export default Investigation;
